import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Coins,
  Sparkles,
  XCircle,
  AlertCircle,
  CheckCircle2,
  Wallet,
  Shield,
  Send,
} from "lucide-react";
import successToast from "../helper-components/success-toast";
import errorToast from "../helper-components/error-toast";
import { canAdd, resolvePolicy } from "./benefit-policy";
import { recomputeBillTotal } from "./billing-totals";
import {
  loyaltyMemberGetAction,
  loyaltyRedeemPreviewAction,
  loyaltyRedeemApplyAction,
  loyaltyOtpRequestAction,
  loyaltyOtpVerifyAction,
} from "../../redux/actions/loyalty-action";

// Compact loyalty panel rendered at the top of the billing payment panel.
//
// Redemption is OTP-gated and online-only:
//
//   1. cashier types points + presses "Send OTP"
//      → POST /loyalty/redeem-otp/request → SMS to customer
//   2. customer reads OTP to cashier; cashier types it + presses "Verify"
//      → POST /loyalty/redeem-otp/verify
//   3. cashier presses "Redeem"
//      → POST /loyalty/redeem (carries the verified otp_request_id + bill_ref)
//      → CPOS walks expiry rows FIFO, stamps the OTP row with the result.
//
// The OTP row is the idempotency record — a retry of the redeem RPC with the
// same bill_ref returns the cached result without double-deducting.

// Module-level cache of in-flight OTP request state keyed by customer
// signature. In-memory only: survives panel close/open (the Dialog unmounts
// the panel) but a page reload re-evaluates this module → cache resets,
// which is the "preserve on close+open / clear on reload" semantic the
// cashier flow wants.
const __otpCache = {};

// Bill save must end the verified session — the next bill needs a fresh OTP
// even for the same customer (the redeem consumed this one anyway).
export const clearLoyaltyOtpCache = () => {
  Object.keys(__otpCache).forEach((k) => delete __otpCache[k]);
};

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;
const fmtINR = (n) => Number(n || 0).toFixed(2);
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

const LoyaltyPanel = ({
  cmrId,
  phoneE164,
  storeId,
  cashierId,
  billTotal,
  billRef,
  paymentData,
  setFieldValue,
  // Full Formik values passed from billing-payment-panel — used by the
  // benefit-policy gate to count active scheme/coupon discounts and by
  // the redeem path that anchors total math on the original snapshot.
  values,
  // Defaults to "sot" (sales-order) but the parent passes whatever the
  // active bill form uses. Same convention as CouponPanel.
  prefixName = "sot",
}) => {
  const dispatch = useDispatch();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redeemInput, setRedeemInput] = useState("");
  const [preview, setPreview] = useState(null);

  // Customer signature for the in-memory OTP cache. Changes on customer
  // switch or when the phone-on-file changes; that key change implicitly
  // invalidates the prior captured OTP state.
  const cmrSig = `${cmrId || ""}|${phoneE164 || ""}`;
  const initialOtp = __otpCache[cmrSig] || null;

  // OTP step state machine: idle → requested → verified → consumed.
  // "consumed" = redeem completed (a Points line is on the bill).
  const [otpStage, setOtpStage] = useState(initialOtp?.stage || "idle");
  const [otpId, setOtpId] = useState(initialOtp?.id || null);
  const [otpCode, setOtpCode] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(initialOtp?.expiresAt || null);
  const [busy, setBusy] = useState(false);

  // Snapshot the phone at OTP-request time so a customer switch mid-flow can't
  // race with an in-flight verify/redeem (those use the ref, not the prop).
  const otpPhoneRef = useRef(initialOtp?.phone || null);
  // Snapshot the points amount used to seed the OTP so the redeem RPC sends
  // exactly the same value the cashier authorised.
  const otpPointsRef = useRef(initialOtp?.points || null);

  // Treats points as a discount line (Formik values.pointsDiscount), with a
  // back-compat fallback to the legacy paymentData "Points" entry for bills
  // that were started before the redeem-as-discount switchover.
  const pointsDiscount = values?.pointsDiscount || null;
  const legacyPointsPay = (paymentData || []).find((p) => p?.payment_mode_name === "Points");
  const existingPointsLine = pointsDiscount
    ? {
        discount_amount: pointsDiscount.discount_amount,
        points_redeemed: pointsDiscount.points,
        pt_value: pointsDiscount.pt_value,
        bill_ref: pointsDiscount.bill_ref,
      }
    : legacyPointsPay
      ? {
          discount_amount: legacyPointsPay.payment_amount,
          points_redeemed: window.__lastPointsRedemption?.points_redeemed,
          pt_value: window.__lastPointsRedemption?.pt_value_applied,
          bill_ref: legacyPointsPay.transaction_id,
        }
      : null;
  const pointsAlreadyRedeemed = existingPointsLine?.points_redeemed ?? null;

  // Member lookup — prefer phone (the new identity), fall back to cmr_id.
  const lookupKey = phoneE164 || cmrId;
  // Track the last (cmrId, phoneE164) signature so we only reset OTP on a
  // genuine customer SWITCH, not on initial mount (which would defeat the
  // session hydration above).
  const lastCmrSigRef = useRef(cmrSig);
  useEffect(() => {
    if (!lookupKey) {
      setMember(null);
      return;
    }
    // Simplified-loyalty model: the balance is fetched LIVE from CPOS (the
    // backend proxies /loyalty/members/:key over the service-token RPC), and
    // it is only fetched once the cashier has STARTED the OTP flow — points
    // are not displayed on mere customer selection. Selecting a customer
    // therefore no longer fires a lookup; see the otpStage effect below.
    if (lastCmrSigRef.current === cmrSig) return;
    lastCmrSigRef.current = cmrSig;
    // Real customer switch — reset OTP state.
    setOtpStage("idle");
    setOtpId(null);
    setOtpCode("");
    setOtpExpiresAt(null);
    otpPhoneRef.current = null;
    otpPointsRef.current = null;
    // Purge other customers' cached OTP state (defense in depth).
    Object.keys(__otpCache).forEach((k) => {
      if (k !== cmrSig) delete __otpCache[k];
    });
    setMember(null);
    window.__loyaltyMember = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey, dispatch, cmrSig]);

  // Live balance fetch — fires only once the OTP flow has started (the
  // customer has implicitly consented at the till). The backend answers with
  // CPOS's live ledger figure, so what the cashier quotes is always current
  // even if the customer earned/redeemed at another store minutes ago.
  useEffect(() => {
    // Balance is pulled and shown ONLY after OTP VERIFICATION — sending the
    // OTP alone reveals nothing. Once verified, the state persists (no
    // re-verify) until page reload, bill save, or a customer change.
    if (!lookupKey || otpStage !== "verified") return;
    if (member) return; // already fetched for this verified session
    setLoading(true);
    dispatch(
      loyaltyMemberGetAction(
        { key: lookupKey },
        (res) => {
          const m = res?.data || null;
          setMember(m);
          // Stash on window so CouponPanel + SchemePanel can read the
          // benefit_policy without duplicating the fetch. Cleared on
          // customer change above.
          window.__loyaltyMember = m;
          setLoading(false);
        },
        () => {
          setMember(null);
          setLoading(false);
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpStage, lookupKey, dispatch]);

  // Mirror OTP request state into the in-memory cache so close/reopen of
  // the payment side panel preserves it. Page reload wipes the cache. The
  // key embeds cmrSig so a customer switch wipes too.
  useEffect(() => {
    // Cache 'requested' AND 'verified' — verified persists across panel
    // close/open until reload, bill save (cleared via clearLoyaltyOtpCache),
    // or a customer switch (cmrSig-keyed purge).
    if ((otpStage === "requested" || otpStage === "verified") && otpId) {
      __otpCache[cmrSig] = {
        stage: otpStage,
        id: otpId,
        expiresAt: otpExpiresAt,
        phone: otpPhoneRef.current,
        points: otpPointsRef.current,
      };
    } else {
      delete __otpCache[cmrSig];
    }
  }, [otpStage, otpId, otpExpiresAt, cmrSig]);

  // NOTE: verified state deliberately survives bill-total changes — the
  // cashier applies points AFTER verification, so cap checks run at Apply
  // time against the current total, and the backend re-validates at save.

  // Client-side computation of max-redeemable points — mirrors validateRedeem.
  const policy = useMemo(() => {
    if (!member) return null;
    const balance = Number(member.points_balance || 0);
    const ptValue = Number(member.pt_value || 0);
    const r = member.redeem_config || {};
    const minRedeem = Math.max(0, Number(r.min_redeem || 0));
    const multipleOf = Math.max(1, Number(r.multiple_of || 1));
    const minBill = Math.max(0, Number(r.min_bill_to_redeem || 0));
    const capPct = Math.max(0, Number(r.effective_cap_pct ?? r.max_pct_of_bill ?? 0));
    const bill = Math.max(0, Number(billTotal || 0));

    const capRupees = bill * (capPct / 100);
    const capPointsFromBill = ptValue > 0 ? Math.floor(capRupees / ptValue) : 0;
    const rawMax = Math.min(balance, capPointsFromBill);
    const maxRoundedToMultiple = Math.floor(rawMax / multipleOf) * multipleOf;

    // Fall back to the phone the engine returned with the loyalty member
    // row — `phoneE164` prop reads `values.cmr_phone_number` which the
    // customer typeahead doesn't always populate, but `member.phone_e164`
    // is always present when the member resolved.
    const effectivePhone = phoneE164 || member?.phone_e164 || null;
    let reason = null;
    if (!effectivePhone) reason = "Customer has no phone number on file.";
    else if (ptValue <= 0) reason = "Redemption not configured.";
    else if (balance <= 0) reason = "No points to redeem.";
    else if (balance < minRedeem) reason = `Need at least ${minRedeem} pts to redeem (you have ${balance}).`;
    else if (bill <= 0) reason = "Add items to bill to redeem points.";
    else if (bill < minBill) reason = `Bill must be ≥ ₹${fmtNum(minBill)} to redeem points.`;
    else if (maxRoundedToMultiple < minRedeem) {
      const minRupees = round2(minRedeem * ptValue);
      reason = `Cap (₹${fmtINR(capRupees)}) is below the ${minRedeem}-pt minimum (₹${fmtINR(minRupees)}).`;
    }

    return {
      balance,
      ptValue,
      minRedeem,
      multipleOf,
      minBill,
      capPct,
      capRupees: round2(capRupees),
      maxPoints: maxRoundedToMultiple,
      maxRupees: round2(maxRoundedToMultiple * ptValue),
      reason,
      canRedeem: !reason,
    };
  }, [member, billTotal, phoneE164]);

  const inputValidation = useMemo(() => {
    if (!policy || !policy.canRedeem) return { ok: false, msg: null };
    const pts = parseInt(redeemInput, 10);
    if (!pts || pts <= 0) return { ok: false, msg: null };
    if (pts < policy.minRedeem) return { ok: false, msg: `Min ${policy.minRedeem} pts` };
    if (policy.multipleOf > 1 && pts % policy.multipleOf !== 0) {
      return { ok: false, msg: `Must be a multiple of ${policy.multipleOf}` };
    }
    if (pts > policy.maxPoints) {
      return { ok: false, msg: `Max ${fmtNum(policy.maxPoints)} pts allowed`, canCap: true };
    }
    return { ok: true, msg: `You save ₹${fmtINR(pts * policy.ptValue)}` };
  }, [redeemInput, policy]);

  useEffect(() => {
    if (!lookupKey || !redeemInput || !policy?.canRedeem) {
      setPreview(null);
      return;
    }
    const pts = parseInt(redeemInput, 10);
    if (!pts || pts <= 0) {
      setPreview(null);
      return;
    }
    const t = setTimeout(() => {
      dispatch(
        loyaltyRedeemPreviewAction(
          { key: lookupKey, bill_total: billTotal || 0, points: pts },
          (res) => setPreview(res?.data || null),
          () => setPreview(null)
        )
      );
    }, 250);
    return () => clearTimeout(t);
  }, [lookupKey, redeemInput, billTotal, dispatch, policy?.canRedeem]);

  // Surface the OTP expiry as a count-down once requested.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (otpStage === "idle") return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [otpStage]);
  const otpSecondsLeft = otpExpiresAt
    ? Math.max(0, Math.floor((new Date(otpExpiresAt).getTime() - now) / 1000))
    : 0;
  useEffect(() => {
    if (otpStage !== "idle" && otpExpiresAt && otpSecondsLeft === 0) {
      // Expired locally — reset.
      setOtpStage("idle");
      setOtpId(null);
      setOtpCode("");
      setOtpExpiresAt(null);
    }
  }, [otpSecondsLeft, otpStage, otpExpiresAt]);

  if (!cmrId && !phoneE164) return null;
  if (loading) {
    return (
      <div className="rounded-xl border border-[#EAE3D2] bg-gradient-to-br from-[#FFFBF0] to-[#FFF6E1] px-4 py-3 mb-3 text-[12px] text-[#9A7B22] flex items-center gap-2">
        <Coins size={14} className="animate-pulse" /> Loading loyalty…
      </div>
    );
  }
  const verifyOtp = () => {
    if (!otpId || !otpCode || otpCode.length < 4) {
      errorToast("Enter the OTP first.");
      return;
    }
    setBusy(true);
    dispatch(
      loyaltyOtpVerifyAction(
        { otp_request_id: otpId, otp_code: otpCode },
        (res) => {
          setBusy(false);
          const data = res?.data || {};
          if (data.ok === false) {
            const c = data.code;
            if (c === "INVALID") {
              errorToast(`Wrong OTP. ${data.attempts_left ?? 0} tries left.`);
            } else if (c === "LOCKED") {
              errorToast("OTP locked after too many attempts. Request a new one.");
              setOtpStage("idle");
              setOtpId(null);
              setOtpCode("");
              setOtpExpiresAt(null);
              otpPhoneRef.current = null;
              otpPointsRef.current = null;
            } else if (c === "EXPIRED") {
              errorToast("OTP expired. Request a new one.");
              setOtpStage("idle");
              setOtpId(null);
              setOtpCode("");
              setOtpExpiresAt(null);
              otpPhoneRef.current = null;
              otpPointsRef.current = null;
            } else {
              errorToast(c || "OTP verify failed.");
            }
            return;
          }
          // Verification UNLOCKS the balance display (the member fetch
          // effect fires on 'verified'); the cashier picks the points and
          // presses Apply afterwards. The verified state persists until
          // reload / bill save / customer change — the redeem itself only
          // consumes the OTP at bill save.
          setOtpStage("verified");
          successToast("Customer verified — points unlocked.");
        },
        (err) => {
          setBusy(false);
          errorToast(err?.message || "OTP verify failed.");
        }
      )
    );
  };

  const sendViewOtp = () => {
    if (!phoneE164) {
      errorToast("Customer has no phone number on file.");
      return;
    }
    setBusy(true);
    otpPhoneRef.current = phoneE164;
    dispatch(
      loyaltyOtpRequestAction(
        { phone_e164: phoneE164, store_id: storeId, requested_by: cashierId || null },
        (res) => {
          setBusy(false);
          const data = res?.data || {};
          setOtpId(data.otp_request_id || null);
          setOtpExpiresAt(data.expires_at || null);
          setOtpCode("");
          setOtpStage("requested");
          successToast(`OTP sent to ${phoneE164}`);
        },
        (err) => {
          setBusy(false);
          errorToast(err?.message || "Could not send OTP — loyalty needs connectivity.");
        }
      )
    );
  };

  if (!member) {
    // Simplified-loyalty model: the balance lives on CPOS and is shown only
    // after the customer VERIFIES via OTP. idle → offer the OTP; requested →
    // code entry; verified → the live fetch fires (effect above) and the
    // full panel renders. The redeem OTP is phone-bound, not points-bound —
    // points are chosen after the balance is visible.
    if (otpStage === "idle") {
      return (
        <div className="rounded-xl border border-[#EAE3D2] bg-gradient-to-br from-[#FFFBF0] to-[#FFF6E1] px-4 py-2.5 mb-3 flex items-center justify-between gap-2">
          <span className="text-[12px] text-[#9A7B22] flex items-center gap-2">
            <Coins size={14} /> Loyalty points — verify customer to view balance
          </span>
          <button
            type="button"
            disabled={busy || !phoneE164}
            title={!phoneE164 ? "Customer has no phone number on file." : undefined}
            onClick={sendViewOtp}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-[#B7791F] text-white disabled:opacity-50"
          >
            {busy ? "…" : "Send OTP"}
          </button>
        </div>
      );
    }
    if (otpStage === "requested") {
      return (
        <div className="rounded-xl border border-[#EAE3D2] bg-gradient-to-br from-[#FFFBF0] to-[#FFF6E1] px-4 py-2.5 mb-3 flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[12px] text-[#9A7B22] flex items-center gap-2">
            <Coins size={14} /> OTP sent to {otpPhoneRef.current || phoneE164}
          </span>
          <span className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={8}
              placeholder="OTP"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
              className="h-8 w-[84px] px-2.5 text-[13px] font-semibold rounded-md border border-[#E5D5A8] bg-white outline-none tabular-nums"
            />
            <button
              type="button"
              onClick={verifyOtp}
              disabled={busy || !otpCode}
              className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-[#B7791F] text-white disabled:opacity-50"
            >
              {busy ? "…" : "Verify"}
            </button>
            <button
              type="button"
              onClick={sendViewOtp}
              disabled={busy}
              className="text-[11px] font-semibold text-[#9A6700] underline disabled:opacity-50"
            >
              Resend
            </button>
          </span>
        </div>
      );
    }
    return (
      <div className="rounded-xl border border-[#E6E9EF] bg-[#F8FAFC] px-4 py-2.5 mb-3 text-[12px] text-[#64748B] flex items-center gap-2">
        <Coins size={14} /> Customer is not enrolled in loyalty.
      </div>
    );
  }

  const tier = member.tier;
  const balance = policy?.balance ?? Number(member.points_balance || 0);
  const ptValue = policy?.ptValue ?? Number(member.pt_value || 0);
  const availableRupees = Number(member.available_rupees || 0);
  const tierColor = tier?.color || "#B7791F";

  const fillMax = () => {
    if (!policy?.canRedeem || policy.maxPoints <= 0) return;
    setRedeemInput(String(policy.maxPoints));
  };


  // Atomic-apply: stash the OTP + points snapshot in Formik. The bill-save
  // dispatch reads pointsDiscount.{phone_e164, points, otp_request_id} and
  // ships them as benefits.points; the backend fires the CPOS
  // /loyalty/redeem-rpc/reserve-and-deduct call INSIDE the bill INSERT
  // transaction. If that RPC fails or the local commit fails after it
  // succeeded, the controller rolls back the bill AND calls
  // /loyalty/redeem-rpc/reverse to undo the CPOS-side burn.
  //
  // The rupee value shown here is a display preview computed locally from
  // the cached member.pt_value. Backend re-computes the canonical amount
  // from the CPOS RPC response and writes that into soit_points_discount.
  const performRedeem = (otpRequestId, pts, phone) => {
    if (!otpRequestId || !pts || pts <= 0) {
      errorToast("Missing OTP context — please request a new OTP.");
      return;
    }
    const ptValueApplied = ptValue;
    const rupees = round2(pts * ptValueApplied);
    const line = {
      points: pts,
      pt_value: ptValueApplied,
      discount_amount: rupees,
      // Atomic-apply identity — bill save ships these as benefits.points:
      phone_e164: phone,
      otp_request_id: otpRequestId,
    };
    setFieldValue("pointsDiscount", line);
    setFieldValue(`${prefixName}_points_discount`, rupees);
    window.__lastPointsRedemption = {
      phone_e164: phoneE164,
      points_redeemed: pts,
      pt_value_applied: ptValueApplied,
      rupees,
    };
    // Drop any legacy paymentData "Points" entry — the breakdown row is
    // the source of truth now.
    const existing = (paymentData || []).filter(
      (p) => p?.payment_mode_name !== "Points"
    );
    if (existing.length !== (paymentData || []).length) {
      setFieldValue("paymentData", existing);
    }
    // Shared recompute keeps coupon + scheme + points consistent. Override
    // covers the same-tick async-batched setFieldValue above.
    recomputeBillTotal({
      values,
      setFieldValue,
      prefixName,
      override: { points: rupees },
    });
    successToast(`Points staged for redemption (${pts} pts / ₹${fmtINR(rupees)}). Applied on save.`);
  };

  const handleClear = () => {
    // Drop the discount line + recompute. The legacy paymentData "Points"
    // entry is also pruned for safety.
    setFieldValue("pointsDiscount", null);
    setFieldValue(`${prefixName}_points_discount`, 0);
    recomputeBillTotal({
      values,
      setFieldValue,
      prefixName,
      override: { points: 0 },
    });
    const next = (paymentData || []).filter(
      (p) => p?.payment_mode_name !== "Points"
    );
    if (next.length !== (paymentData || []).length) {
      setFieldValue("paymentData", next);
    }
    successToast("Points redemption removed");
  };

  const disabled = !policy?.canRedeem;
  const inputPts = Math.max(0, parseInt(redeemInput, 10) || 0);
  const capProgress =
    policy?.maxPoints > 0 ? Math.min(100, (inputPts / policy.maxPoints) * 100) : 0;

  // ---- Render: redeemed-already state -----------------------------------
  if (existingPointsLine) {
    return (
      <div className="rounded-xl border border-[#BBE7CC] bg-gradient-to-br from-[#F0FBF4] to-[#E6F6EC] px-3 py-2.5 mb-3 flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm"
          style={{ background: "linear-gradient(135deg, #22C55E, #15803C)", color: "white" }}
        >
          <CheckCircle2 size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-[#15803C] font-bold">
            Points applied
          </div>
          <div className="text-[14px] font-bold text-[#0F172A] mt-0.5 flex items-center gap-2 flex-wrap">
            <span>
              {pointsAlreadyRedeemed != null ? `${fmtNum(pointsAlreadyRedeemed)} pts` : ""}
            </span>
            <span className="text-[13px] text-[#15803C]">
              − ₹{fmtINR(existingPointsLine.discount_amount)}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClear}
          title="Remove redemption"
          className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md text-[12px] font-semibold text-[#15803C] hover:bg-white/60"
        >
          <XCircle size={14} /> Remove
        </button>
      </div>
    );
  }

  // ---- Render: main panel -----------------------------------------------
  return (
    <div
      className={`rounded-xl border mb-3 overflow-hidden transition-shadow ${
        disabled
          ? "border-[#FDE8C7] bg-gradient-to-br from-[#FFFBF0] to-[#FFF6E1]"
          : "border-[#EFE6CC] bg-gradient-to-br from-[#FFFBEB] to-[#FFF4D6] shadow-[0_1px_2px_rgba(183,121,31,0.06)]"
      }`}
    >
      {/* Top row — brand + balance + points input + Max + OTP/Redeem */}
      <div className="flex items-center gap-3 flex-wrap px-3.5 py-2.5">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-[0_2px_4px_rgba(183,121,31,0.2)]"
            style={{
              background: `linear-gradient(135deg, ${tierColor}, ${tierColor}cc)`,
              color: "white",
            }}
          >
            <Coins size={18} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[10.5px] font-bold tracking-wider uppercase text-[#9A7B22]">
                Loyalty
              </span>
              {tier && (
                <span
                  className="px-1.5 py-[1px] rounded-full text-[10px] font-bold leading-tight"
                  style={{ background: `${tierColor}22`, color: tierColor }}
                >
                  {tier.name} · {tier.multiplier}×
                </span>
              )}
            </div>
            <div className="text-[15px] font-extrabold text-[#0F172A] mt-[1px] leading-tight">
              {fmtNum(balance)}{" "}
              <span className="text-[11px] font-semibold text-[#64748B]">pts</span>
              <span className="ml-1.5 text-[11.5px] font-semibold text-[#9A7B22]">
                ≈ ₹{fmtINR(availableRupees)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-[6px]" />

        <div className="flex items-center gap-1.5">
          <div
            className={`flex items-stretch rounded-md overflow-hidden border ${
              disabled
                ? "border-[#EAE3D2] bg-white/50"
                : redeemInput && !inputValidation.ok
                ? "border-[#FCA5A5] bg-white"
                : redeemInput && inputValidation.ok
                ? "border-[#86EFAC] bg-white"
                : "border-[#E5D5A8] bg-white focus-within:border-[#B7791F] focus-within:ring-2 focus-within:ring-[#B7791F]/15"
            }`}
          >
            <input
              type="number"
              placeholder={
                disabled
                  ? "—"
                  : policy?.multipleOf > 1
                  ? `step ${policy.multipleOf}`
                  : "pts"
              }
              value={redeemInput}
              onChange={(e) => setRedeemInput(e.target.value)}
              disabled={disabled}
              step={policy?.multipleOf || 1}
              min={policy?.minRedeem || 0}
              max={policy?.maxPoints || 0}
              title={disabled ? policy?.reason : undefined}
              className={`h-9 w-[88px] px-2.5 text-[13px] font-semibold bg-transparent outline-none ${
                disabled ? "text-[#94A3B8] cursor-not-allowed" : "text-[#0F172A]"
              }`}
            />
            <span
              className={`flex items-center pr-2 text-[11px] font-semibold ${
                disabled ? "text-[#94A3B8]" : "text-[#9A7B22]"
              }`}
            >
              pts
            </span>
          </div>

          <button
            type="button"
            onClick={fillMax}
            disabled={disabled || (policy && policy.maxPoints <= 0)}
            title={
              disabled
                ? policy?.reason
                : `Use ${fmtNum(policy.maxPoints)} pts (≈ ₹${fmtINR(policy.maxRupees)})`
            }
            className="h-9 px-2.5 inline-flex items-center gap-1 rounded-md text-[12px] font-bold border bg-white border-[#E5D5A8] text-[#9A6700] hover:bg-[#FFF8DD] hover:border-[#D4A52A] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Sparkles size={12} /> Max
          </button>

          {/* Member visible ⇒ customer already OTP-verified. Apply stashes
              the points into Formik (performRedeem); the OTP is consumed at
              bill save. Same verified session covers re-applies until
              reload / save / customer change. */}
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#E6F6EC] text-[#15803C] text-[11.5px] font-bold">
            <Shield size={12} /> Verified
          </span>
          <button
            type="button"
            onClick={() => {
              const pts = parseInt(redeemInput, 10);
              if (!pts || !inputValidation.ok) {
                errorToast(inputValidation.msg || "Enter a valid points amount first.");
                return;
              }
              const policyName = resolvePolicy(member);
              const ok = canAdd("points", values, policyName);
              if (!ok.ok) {
                errorToast(ok.reason);
                return;
              }
              performRedeem(otpId, pts, otpPhoneRef.current || phoneE164 || member?.phone_e164);
            }}
            disabled={
              busy || disabled || !redeemInput || !inputValidation.ok || !otpId
            }
            className="h-9 px-3.5 inline-flex items-center gap-1.5 font-bold rounded-md bg-primary text-white text-[13px] hover:bg-primary/90 disabled:bg-[#CBD5E1] disabled:cursor-not-allowed shadow-[0_1px_2px_rgba(39,125,254,0.25)]"
          >
            {busy ? "…" : (<><Wallet size={13} /> Apply</>)}
          </button>
        </div>
      </div>

      {/* Lower strip — reasons / progress / OTP countdown */}
      <div className="px-3.5 pb-2.5 -mt-1 space-y-1.5">
        {disabled ? (
          <div className="flex items-start gap-1.5 text-[11.5px] text-[#9A6700] bg-white/60 rounded-md px-2.5 py-1.5 border border-[#FDE8C7]">
            <AlertCircle size={13} className="mt-[1px] shrink-0" /> {policy.reason}
          </div>
        ) : (
          <>
            <div className="h-1 rounded-full bg-[#F4E7C0] overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  inputValidation.ok
                    ? "bg-gradient-to-r from-[#22C55E] to-[#15803C]"
                    : "bg-gradient-to-r from-[#D4A52A] to-[#B7791F]"
                }`}
                style={{ width: `${capProgress}%` }}
              />
            </div>
            <div className="flex items-center justify-between gap-2 text-[11px] flex-wrap">
              <div className="text-[#64748B]">
                Up to{" "}
                <button
                  type="button"
                  onClick={fillMax}
                  disabled={otpStage !== "idle"}
                  className="font-bold text-[#9A6700] underline-offset-2 hover:underline disabled:no-underline disabled:cursor-not-allowed"
                >
                  {fmtNum(policy.maxPoints)} pts
                </button>{" "}
                <span className="text-[#9A7B22] font-semibold">
                  (₹{fmtINR(policy.maxRupees)})
                </span>{" "}
                <span className="text-[#94A3B8]">· {policy.capPct}% of bill</span>
              </div>
              <div className="flex items-center gap-1">
                {policy.multipleOf > 1 && (
                  <span className="px-1.5 py-[1px] rounded bg-white border border-[#EAE3D2] text-[10px] font-semibold text-[#9A7B22]">
                    × {policy.multipleOf}
                  </span>
                )}
                {policy.minRedeem > 0 && (
                  <span className="px-1.5 py-[1px] rounded bg-white border border-[#EAE3D2] text-[10px] font-semibold text-[#9A7B22]">
                    min {policy.minRedeem}
                  </span>
                )}
              </div>
            </div>
            {redeemInput && inputValidation.msg && (
              <div
                className={`flex items-center gap-1.5 text-[12px] font-semibold ${
                  inputValidation.ok ? "text-[#15803C]" : "text-[#B91C1C]"
                }`}
              >
                {inputValidation.ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
                <span>{inputValidation.msg}</span>
                {inputValidation.canCap && (
                  <button
                    type="button"
                    onClick={fillMax}
                    className="text-primary underline font-bold ml-1"
                  >
                    Use max
                  </button>
                )}
              </div>
            )}

            {otpStage === "requested" && (
              <div className="flex items-center gap-2 text-[11.5px] text-[#475569] bg-white/60 rounded-md px-2.5 py-1.5 border border-[#E0E7FF]">
                <Shield size={13} className="text-[#4F46E5]" />
                <span>
                  OTP sent to <span className="font-semibold">{otpPhoneRef.current || phoneE164}</span>. Valid for{" "}
                  <span className="font-bold tabular-nums">
                    {String(Math.floor(otpSecondsLeft / 60)).padStart(2, "0")}:
                    {String(otpSecondsLeft % 60).padStart(2, "0")}
                  </span>
                  .
                </span>
              </div>
            )}

            {otpStage === "verified" && (
              <div className="flex items-center gap-2 text-[11.5px] text-[#15803C] bg-[#F0FBF4] rounded-md px-2.5 py-1.5 border border-[#BBE7CC]">
                <CheckCircle2 size={13} />
                <span>OTP verified — press Redeem to apply {fmtNum(inputPts)} pts.</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoyaltyPanel;
